import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

import { getImageUrl } from '@/lib/polaroid-utils';
import { isValidAlbum } from '@/lib/album-utils';

/** Validate a feed event — kind 20 with valid image or kind 30006 album. */
function isValidFeedEvent(event: NostrEvent): boolean {
  if (event.kind === 30006) return isValidAlbum(event);
  if (event.kind !== 20) return false;
  return !!getImageUrl(event);
}

const PAGE_SIZE = 15;

/** Query kind 20 picture events and kind 30006 albums by a specific author with infinite scroll pagination. */
export function useUserPicturesInfinite(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useInfiniteQuery({
    queryKey: ['pictures', 'user-infinite', pubkey ?? ''],
    queryFn: async ({ pageParam }) => {
      if (!pubkey) return [];
      const filter: NostrFilter = { kinds: [20, 30006], authors: [pubkey], limit: PAGE_SIZE + 5 };
      if (pageParam) {
        filter.until = pageParam;
      }

      const events = await nostr.query(
        [filter],
        { signal: AbortSignal.timeout(5000) },
      );

      return events.filter(isValidFeedEvent).slice(0, PAGE_SIZE);
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      return lastPage[lastPage.length - 1].created_at - 1;
    },
    initialPageParam: undefined as number | undefined,
    enabled: !!pubkey,
    staleTime: 60 * 1000,
  });
}
